/** Cumulative resolver lives outside draft undo; a tree edit rebuilds it. */
import { useMemo, useState } from "react";
import type { Flag, StudioItem } from "../domain/studioTypes";
import {
  attachResolver,
  blockOrderSignature,
  blockOrderViolations,
  fragmentsSatisfied,
  groupsInOrder,
  gradientResidue,
  intrudersCleared,
  relocateIntruders,
  rejoinFragments,
  relocateOrderOutliers,
  resequenceFallback,
  sizeOrderSatisfied,
  snapshotPlan,
  type ResolverAuditSummary,
  type ResolverStep,
} from "./cdtResolve";
import { resolvedLayout } from "./resolvedLayout";
import { RESOLVER_FLAG_KINDS, type ResolverInputs } from "./toResolverInputs";

export interface AutoResolveApi {
  applied: Readonly<Record<string, ResolverStep>>;
  layout: ReturnType<typeof resolvedLayout>;
  baseAudit: ResolverAuditSummary | null;
  currentAudit: ResolverAuditSummary | null;
  inputs: ResolverInputs;
  movedCount: number;
  hasChanges: boolean;
  handles: (flag: Flag) => boolean;
  isSatisfied: (flag: Flag, pinOverride?: ResolverInputs["pinByFlagId"][string]) => boolean;
  resolveFlag: (
    flag: Flag,
    pinOverride?: ResolverInputs["pinByFlagId"][string],
  ) => ResolverStep | null;
  resolveAllFlags: () => readonly ResolverStep[];
  reset: () => void;
}

export const GRADE_STEP_KEY = "__grade__";

/** Pog-keyed so unmounting the studio does not revert applied fixes. */
interface ResolverCacheEntry {
  studio: ReturnType<typeof attachResolver> | null;
  applied: Readonly<Record<string, ResolverStep>>;
  inputsSignature: string;
}
const RESOLVER_CACHE = new WeakMap<object, ResolverCacheEntry>();

/** Close / Reset / external plan replace — otherwise a step stays applied. */
export const clearResolverCache = (cacheKey: object | undefined): void => {
  if (cacheKey) RESOLVER_CACHE.delete(cacheKey);
};

const inputsSignatureOf = (inputs: ResolverInputs): string =>
  JSON.stringify({
    itemsDoc: inputs.itemsDoc,
    runs: inputs.canvas.runs,
    geometry: inputs.canvas.geo,
    bayWidth: inputs.canvas.bayW,
    pins: inputs.pins,
    runWidth: inputs.runW,
    quantityAttribute: inputs.quantityAttribute,
    verticalBlockShare: inputs.verticalBlockShare,
    blockIdentityDepths: inputs.blockIdentityDepths,
    terminalVariantDepth: inputs.terminalVariantDepth,
  });

export const useAutoResolve = (
  items: readonly StudioItem[],
  inputs: ResolverInputs,
  flags: readonly Flag[],
  cacheKey?: object,
): AutoResolveApi => {
  const signature = useMemo(() => inputsSignatureOf(inputs), [inputs]);
  const studio = useMemo(() => {
    const cached = cacheKey ? RESOLVER_CACHE.get(cacheKey) : undefined;
    if (cached && cached.inputsSignature === signature) return cached.studio;
    try {
      const fresh = attachResolver(inputs.canvas, inputs.itemsDoc, {
        runW: inputs.runW,
        ...(inputs.sizeRule ? { sizeRule: inputs.sizeRule } : {}),
      });
      if (cacheKey) {
        RESOLVER_CACHE.set(cacheKey, { studio: fresh, applied: {}, inputsSignature: signature });
      }
      return fresh;
    } catch {
      return null;
    }
  }, [inputs, signature, cacheKey]);

  const [applied, setAppliedState] = useState<Readonly<Record<string, ResolverStep>>>(() =>
    cacheKey ? (RESOLVER_CACHE.get(cacheKey)?.applied ?? {}) : {},
  );
  const setApplied = (
    update: (
      current: Readonly<Record<string, ResolverStep>>,
    ) => Readonly<Record<string, ResolverStep>>,
  ) => {
    setAppliedState((current) => {
      const next = update(current);
      if (cacheKey) {
        const entry = RESOLVER_CACHE.get(cacheKey);
        if (entry) entry.applied = next;
      }
      return next;
    });
  };
  // New resolver starts from as-built; stale steps must not linger.
  const [appliedFor, setAppliedFor] = useState(studio);
  if (appliedFor !== studio) {
    setAppliedFor(studio);
    setAppliedState(cacheKey ? (RESOLVER_CACHE.get(cacheKey)?.applied ?? {}) : {});
  }

  const positions = useMemo(
    () => (studio && Object.keys(applied).length > 0 ? studio.positions() : {}),
    [studio, applied],
  );
  const layout = useMemo(() => resolvedLayout(items, positions), [items, positions]);

  const baseAudit = useMemo(() => {
    try {
      return studio?.audit() ?? null;
    } catch {
      return null;
    }
  }, [studio]);
  const currentAudit = useMemo(() => {
    try {
      return studio?.audit() ?? null;
    } catch {
      return null;
    }
  }, [studio, applied]);

  const satisfiesPin = (
    pin: NonNullable<ResolverInputs["pinByFlagId"][string]>,
    flagKind?: string,
  ): boolean => {
    if (!studio) return false;
    try {
      if (pin.fragment) return fragmentsSatisfied(studio, pin);
      if (pin.seqOrder && pin.seqOrder.length > 1) {
        if (pin.kind === "size-order") return sizeOrderSatisfied(studio, pin);
        return studio.check(pin).resolved && groupsInOrder(studio, pin);
      }
      // Flag kind discriminates; vendored check is depth-2.
      if (flagKind === "contiguity") {
        return studio.check(pin).resolved && intrudersCleared(studio, pin);
      }
      return studio.check(pin).resolved;
    } catch {
      return false;
    }
  };

  const resolveFragmentPin = (
    pin: NonNullable<ResolverInputs["pinByFlagId"][string]>,
  ): ResolverStep => {
    const step = rejoinFragments(studio!, pin);
    if (step) return { ...step, resolved: fragmentsSatisfied(studio!, pin) };
    return {
      pin: pin.scope,
      kind: pin.kind,
      action: "none",
      ok: true,
      resolved: true,
      alreadyClean: true,
      physicalChange: false,
      notes: [],
      diff: { introduced: 0 },
    };
  };

  const resolveFlag = (
    flag: Flag,
    pinOverride?: ResolverInputs["pinByFlagId"][string],
  ): ResolverStep | null => {
    const pin = pinOverride ?? inputs.pinByFlagId[flag.id];
    if (!studio || !pin) return null;
    if (pin.fragment) {
      const step = resolveFragmentPin(pin);
      setApplied((current) => ({ ...current, [flag.id]: step }));
      return step;
    }
    const rejoinNotes: string[] = [];
    let rejoined = false;
    if (pin.seqOrder && pin.seqOrder.length > 1) {
      const rejoin = rejoinFragments(studio, pin);
      if (rejoin) {
        rejoined = rejoin.physicalChange === true;
        rejoinNotes.push(...rejoin.notes, ...(rejoin.reason ? [rejoin.reason] : []));
      }
    }
    let step = studio.resolve(pin);
    // Vendored check calls intruders already satisfied.
    if (flag.kind === "contiguity" && !intrudersCleared(studio, pin)) {
      const rotation = relocateIntruders(studio, pin);
      if (rotation) {
        step = {
          ...step,
          ...rotation,
          notes: [...step.notes, ...rotation.notes],
          alreadyClean: false,
        };
      }
    }
    if (pin.seqOrder && pin.seqOrder.length > 1) {
      const residue = rejoinFragments(studio, pin);
      if (residue?.physicalChange) {
        rejoined = true;
        rejoinNotes.push(...residue.notes);
      }
    }
    // Rejoin can settle the sequence; "already clean" would misread.
    if (rejoined && step.alreadyClean) {
      step = {
        ...step,
        alreadyClean: false,
        physicalChange: true,
        action: "relocate",
        reason: undefined,
      };
    }
    if (rejoinNotes.length) step = { ...step, notes: [...rejoinNotes, ...step.notes] };
    if (pin.seqOrder && pin.seqOrder.length > 1) {
      const outliers = relocateOrderOutliers(studio, pin);
      if (outliers?.physicalChange) {
        rejoinNotes.push(...outliers.notes);
        rejoined = true;
        step = { ...step, resolved: satisfiesPin(pin), alreadyClean: false };
      }
    }
    if (pin.seqOrder && pin.seqOrder.length > 1 && !step.resolved && !satisfiesPin(pin)) {
      if (!step.resolved && !groupsInOrder(studio, pin)) {
        const fallback = resequenceFallback(studio, pin);
        if (fallback.physicalChange || step.alreadyClean) {
          step = { ...fallback, resolved: satisfiesPin(pin) };
        }
      }
      if (!step.resolved) {
        const alongClean = groupsInOrder(studio, pin);
        const residue = gradientResidue(studio, pin);
        const gradeNote = step.notes.find((note) => /gradient|re-hang/i.test(note));
        const parts: string[] = [];
        parts.push(
          alongClean
            ? "each shelf now reads in order"
            : "the remaining sequence break is a stray fragment this footprint cannot rejoin",
        );
        if (pin.kind === "size-order" && residue > 0) {
          parts.push(
            `${residue} cross-shelf inverted pair(s) remain — ` +
              (gradeNote ?? "re-hanging them would worsen the gradient"),
          );
        }
        step = { ...step, partial: true, reason: parts.join("; ") };
      }
    }
    setApplied((current) => ({ ...current, [flag.id]: step }));
    return step;
  };

  const resolveAllFlags = (): readonly ResolverStep[] => {
    if (!studio) return [];
    // Resolves never change block order; if the pass does, restore and go pin-by-pin.
    const orderBefore = blockOrderSignature(studio);
    const restoreAll = snapshotPlan(studio);
    const stepByFlagId: Record<string, ResolverStep> = {};
    const runFragments = () => {
      for (const flag of flags) {
        const pin = inputs.pinByFlagId[flag.id];
        if (!pin?.fragment) continue;
        stepByFlagId[flag.id] = resolveFragmentPin(pin);
      }
    };
    runFragments();
    let { steps } = studio.resolveAll();
    if (blockOrderViolations(orderBefore, blockOrderSignature(studio)).length) {
      restoreAll();
      runFragments();
      const conservative: ResolverStep[] = [];
      for (const flag of flags) {
        const pin = inputs.pinByFlagId[flag.id];
        if (!pin || pin.fragment) continue;
        const restoreStep = snapshotPlan(studio);
        const step = studio.resolve(pin);
        if (blockOrderViolations(orderBefore, blockOrderSignature(studio)).length) {
          restoreStep();
          conservative.push({
            ...step,
            ok: true,
            resolved: false,
            physicalChange: false,
            reason: "this fix would change the CDT block order — left as built",
          });
          continue;
        }
        conservative.push(step);
      }
      steps = conservative;
    }
    // One node can carry a location pin and an order pin.
    const flagByPin = new Map(
      flags.map((flag) => {
        const pin = inputs.pinByFlagId[flag.id];
        return [`${pin?.kind ?? ""}#${pin?.scope ?? ""}`, flag.id];
      }),
    );
    let gradeStep: ResolverStep | null = null;
    for (const step of steps) {
      if (step.action === "grade" && step.pin === "every block") {
        gradeStep = step;
        continue;
      }
      const flagId = flagByPin.get(`${step.kind}#${step.pin}`);
      if (flagId) stepByFlagId[flagId] = step;
    }
    // Per-shelf can pass while groups do not.
    for (const flag of flags) {
      const pin = inputs.pinByFlagId[flag.id];
      if (!pin || !pin.seqOrder || pin.seqOrder.length < 2) continue;
      const step = stepByFlagId[flag.id];
      if (step && step.resolved) continue;
      if (groupsInOrder(studio, pin)) continue;
      stepByFlagId[flag.id] = resequenceFallback(studio, pin);
    }
    for (const flag of flags) {
      const pin = inputs.pinByFlagId[flag.id];
      if (!pin || pin.fragment || flag.kind !== "contiguity") continue;
      if (intrudersCleared(studio, pin)) continue;
      const rotation = relocateIntruders(studio, pin);
      if (rotation) stepByFlagId[flag.id] = rotation;
    }
    setApplied((current) => ({
      ...current,
      ...stepByFlagId,
      ...(gradeStep ? { [GRADE_STEP_KEY]: gradeStep } : {}),
    }));
    return steps;
  };

  return {
    applied,
    layout,
    baseAudit,
    currentAudit,
    inputs,
    movedCount: Object.keys(positions).length,
    hasChanges: Object.keys(positions).length > 0,
    handles: (flag) => RESOLVER_FLAG_KINDS.has(flag.kind),
    isSatisfied: (flag, pinOverride) => {
      const pin = pinOverride ?? inputs.pinByFlagId[flag.id];
      if (!pin) return false;
      return satisfiesPin(pin, flag.kind);
    },
    resolveFlag,
    resolveAllFlags,
    reset: () => {
      studio?.reset();
      setApplied(() => ({}));
    },
  };
};
